<script setup lang="ts">
import type { ChatAgentHistoryItemData } from '@dashhudson/dashing-ui/chat-agent-history';
import { ChatAgentHistory, extendedChatDateGroup } from '@dashhudson/dashing-ui/chat-agent-history';
import { useDashToast } from '@dashhudson/dashing-ui/toast';
import { computed } from 'vue';

import type { Conversation } from '@/components/VisionAi/chat-panel-components/utils/VisionChatSidePanel.types';
import { useDeleteConversationConfirmation } from '@/components/VisionAi/composables/useDeleteConversationConfirmation';
import { useNewChatDisabled } from '@/components/VisionAi/composables/useNewChatDisabled';
import RenameConversationPopup from '@/components/VisionAi/RenameConversationPopup.vue';
import { useVisionAiStore } from '@/stores/vision-ai';
import { useVisionAiConversationsStore } from '@/stores/vision-ai-conversations';

const visionAiStore = useVisionAiStore();
const visionAiConversationsStore = useVisionAiConversationsStore();
const { addToast } = useDashToast({ life: 5000 });

const { newChatDisabled, newChatTooltip } = useNewChatDisabled();

const { confirmDeleteConversation } = useDeleteConversationConfirmation();

// Mirror the store's currentConversationId through a computed so the sidebar
// highlight stays in sync regardless of who changed it (user click, button pill,
// "New Chat", etc). Only a user-driven v-model write runs the setter path, so
// programmatic store changes don't double-fetch messages.
const selectedConversationId = computed<string | number | null>({
  get: () => visionAiConversationsStore.currentConversationId,
  set: async (conversationId) => {
    if (conversationId == null) return;
    const previousId = visionAiConversationsStore.currentConversationId;
    visionAiConversationsStore.setCurrentConversationId(conversationId);
    try {
      await visionAiConversationsStore.getConversationMessages({ conversationId });
      visionAiStore.markConversationRead(conversationId);
    } catch {
      // Revert the selection so the highlight matches the messages still on screen,
      // but only if the user hasn't moved on to another conversation in the meantime.
      if (visionAiConversationsStore.currentConversationId === conversationId) {
        visionAiConversationsStore.setCurrentConversationId(previousId);
      }
      addToast({
        message: 'Could not load this conversation. Please try again.',
        severity: 'error',
      });
    }
  },
});

const conversations = computed(() =>
  (visionAiConversationsStore.conversations as Conversation[]).map((conversation) => ({
    id: conversation.id,
    title: conversation.name || 'New Chat',
    createdAt: Temporal.Instant.from(conversation.createdAt),
    alert: visionAiStore.isConversationUnread(conversation.id),
  })),
);

function handleNewChat() {
  visionAiStore.startNewChat();
}

function handleRename(item: ChatAgentHistoryItemData) {
  visionAiConversationsStore.openRenameConversationWidget({ conversationId: item.id });
}

function handleDelete(item: ChatAgentHistoryItemData) {
  confirmDeleteConversation({ conversationId: item.id, isSummary: false });
}
</script>

<template>
  <ChatAgentHistory
    v-model="selectedConversationId"
    :items="conversations"
    :group-by="extendedChatDateGroup"
    :storage-key="'vision-chat-history'"
    :new-chat-disabled="newChatDisabled"
    :new-chat-tooltip="newChatTooltip"
    @new-chat="handleNewChat"
    @rename="handleRename"
    @delete="handleDelete"
  />
  <RenameConversationPopup />
</template>
